import React, { useState } from "react";
import ConfirmModal from "../modals/ConfirmModal.jsx";
import { showErrorToast, showPromiseToast } from "../../services/toastService.js";
import { doActionMail } from "../../services/communicationsService.js";
import LoadingSpinner from "../LoadingSpinner.jsx";
import { formatDate } from "../../utils/dateUtils.js";

const SEND_MODE_SINGLE = "single";
const SEND_MODE_ALL = "all";
const SEND_MODE_USERS = "users";

const MailManagement = ({ state, setState, setIsDirty }) => {
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [formData, setFormData] = useState({});
	const [sendMode, setSendMode] = useState(SEND_MODE_USERS);
	const [recipients, setRecipients] = useState([""]);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [confirmationMessage, setConfirmationMessage] = useState("");
	const [confirmAction, setConfirmAction] = useState(() => {});
	const [subject, setSubject] = useState("");

	const handleTemplateSelect = (templatePath) => {
		if (!templatePath) {
			setSelectedTemplate(null);
			setFormData({});
			setSubject("");
			setIsDirty(false);
			return;
		}

		const template = state.templates.find((t) => t.path === templatePath);
		if (template) {
			setSelectedTemplate(template);
			setFormData(
				template.placeholders.reduce((acc, ph) => {
					acc[ph.placeholder] = ph.required ? "" : null;
					return acc;
				}, {})
			);
			setSubject(template.subject);
			setIsDirty(false);
		}
	};

	const handleFormChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		setIsDirty(true);
	};

	const validateForm = () => {
		const missingFields = selectedTemplate.placeholders
			.filter(
				(ph) =>
					ph.required &&
					(!formData[ph.placeholder] || formData[ph.placeholder].trim() === "")
			)
			.map((ph) => ph.name);

		if (missingFields.length > 0) {
			showErrorToast(`Campi obbligatori mancanti: ${missingFields.join(", ")}`);
			return false;
		}

		if (sendMode === SEND_MODE_SINGLE && !recipients.some((email) => email.trim())) {
			showErrorToast("Devi specificare almeno un destinatario se non invii a tutti.");
			return false;
		}

		return true;
	};

	const handleSendMail = () => {
		if (!validateForm()) return;

		setConfirmationMessage("Sei sicuro di voler inviare questa email?");
		setConfirmAction(() => confirmSendMail);
		setShowConfirmModal(true);
	};

	const confirmSendMail = () => {
		const recipientList =
			sendMode === SEND_MODE_SINGLE
				? recipients.filter((email) => email.trim()).map((email) => email.trim())
				: null;
		showPromiseToast(
			doActionMail({
				action: "sendMail",
				template: selectedTemplate.path,
				data: formData,
				sendAll: sendMode === SEND_MODE_ALL,
				sendByLevel: sendMode === SEND_MODE_USERS ? 10 : null,
				recipients: recipientList,
				subject,
			}),
			{
				pending: "Invio email in corso...",
				success: "Email inviata con successo",
			}
		)
			.then(() => {
				setShowConfirmModal(false);
				setSelectedTemplate(null);
				setFormData({});
				setRecipients([""]);
				setSendMode(SEND_MODE_ALL);
				setSubject("");
				setIsDirty(false);
			})
			.catch((error) => {
				showErrorToast(`Errore durante l'invio dell'email: ${error.message}`);
				setShowConfirmModal(false);
			});
	};

	const handleAlertMail = (alert) => {
		setConfirmationMessage(
			`Sei sicuro di voler inviare una mail per l'allerta: ${alert.title}?`
		);
		setConfirmAction(() => () => confirmAlertMail(alert));
		setShowConfirmModal(true);
	};

	const confirmAlertMail = (alert) => {
		showPromiseToast(
			doActionMail({ action: "mailAlert", alertId: alert.id, from: alert.alert_from }),
			{
				pending: "Invio email in corso...",
				success: "Email inviata con successo",
			}
		)
			.then(() => {
				const updatedAlerts = state.alerts.filter((a) => a.id !== alert.id) || [];
				setState({ ...state, alerts: updatedAlerts });
			})
			.finally(() => setShowConfirmModal(false));
	};

	const handleIgnoreAlert = (alert) => {
		setConfirmationMessage(`Sei sicuro di voler ignorare l'allerta: ${alert.title}?`);
		setConfirmAction(() => () => confirmIgnoreAlert(alert));
		setShowConfirmModal(true);
	};

	const confirmIgnoreAlert = (alert) => {
		showPromiseToast(
			doActionMail({ action: "ignoreAlert", alertId: alert.id, from: alert.alert_from }),
			{
				pending: "Richiesta in corso...",
				success: "Allerta ignorata con successo",
			}
		)
			.then(() => {
				const updatedAlerts = state.alerts.filter((a) => a.id !== alert.id) || [];
				setState({ ...state, alerts: updatedAlerts });
			})
			.finally(() => setShowConfirmModal(false));
	};

	const handleRecipientChange = (index, value) => {
		setRecipients((prev) => {
			const updated = [...prev];
			updated[index] = value;
			return updated;
		});
		if (recipients.every((r) => r.trim() !== "")) setRecipients((prev) => [...prev, ""]);
	};

	if (state.alerts === undefined || state.templates === undefined) return <LoadingSpinner />;
	return (
		<div>
			<h2 className="text-2xl font-bold mb-4 text-center">Gestione Comunicazioni</h2>

			{!selectedTemplate && (
				<div className="mb-6">
					<h3 className="text-xl font-semibold mb-2">Invia Email da Allerta</h3>
					{state.alerts.length > 0 ? (
						<ul className="space-y-4">
							{state.alerts.map((alert) => (
								<li
									key={alert.id}
									className="border border-gray-300 bg-white p-4 rounded flex justify-between items-center"
								>
									<div>
										<h4 className="font-bold">
											<span className="uppercase">{alert.alert_from}</span> -{" "}
											{alert.title} (Priorità: {alert.priority})
										</h4>
										<p>
											{formatDate(alert.valid_date)} -{" "}
											{formatDate(alert.expire_date)}
										</p>
										<p>{alert.description}</p>
									</div>
									<div>
										<button
											id="ignore"
											aria-label="Ignora"
											onClick={() => handleIgnoreAlert(alert)}
											className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 mx-2"
										>
											Ignora
										</button>
										<button
											id="mail"
											aria-label="Invia Mail"
											onClick={() => handleAlertMail(alert)}
											className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 mx-2"
										>
											Invia Mail
										</button>
									</div>
								</li>
							))}
						</ul>
					) : (
						<p className="text-gray-500">Nessuna allerta disponibile.</p>
					)}
				</div>
			)}

			{!selectedTemplate && (
				<div className="mb-6">
					<h3 className="text-xl font-semibold mb-2">Seleziona un Template</h3>
					<div className="rounded w-full list-disc list-inside">
						{state.templates.map((template) => (
							<div
								key={template.id}
								onClick={() => handleTemplateSelect(template.path)}
								className="p-3 rounded bg-white hover:bg-blue-400  border-gray-800 border-2 cursor-pointer m-2 text-blue-600 hover:text-white"
							>
								<span className="font-bold">{template.name}</span>
								<span>
									{template.placeholders.map(
										(ph) =>
											` [${ph.name.toLowerCase()}${ph.required ? "*" : ""}] `
									)}
								</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Modifica Subject */}
			{selectedTemplate && (
				<div className="mb-6">
					<h3 className="text-xl font-semibold mb-2">
						Template: <span className="italic">{selectedTemplate.name}</span>
					</h3>
					<label className="block font-medium mb-1">Oggetto</label>
					<input
						type="text"
						value={subject}
						onChange={(e) => setSubject(e.target.value)}
						className="border border-gray-300 p-2 rounded w-full"
					/>
				</div>
			)}

			{/* Form Dinamico per i Placeholder */}
			{selectedTemplate && (
				<div className="mb-6">
					<h3 className="text-xl font-semibold mb-2">Campi del template</h3>
					{selectedTemplate.placeholders.map((ph) => (
						<div key={ph.id} className="mb-4">
							<label className="block font-medium mb-1">
								{ph.name}{" "}
								{ph.required === 1 && <span className="text-red-500">*</span>}
							</label>
							<input
								type="text"
								name={ph.placeholder}
								value={formData[ph.placeholder] || ""}
								onChange={handleFormChange}
								className="border border-gray-300 p-2 rounded w-full"
								required={ph.required}
							/>
						</div>
					))}

					{/* Opzioni Destinatari */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold mb-2">Destinatari</h3>
						<div className="flex items-center gap-4">
							<label className="flex items-center gap-2">
								<input
									type="radio"
									name="recipientsOption"
									checked={sendMode === SEND_MODE_ALL}
									onChange={() => setSendMode(SEND_MODE_ALL)}
								/>
								Invia a tutti
							</label>
							<label className="flex items-center gap-2">
								<input
									type="radio"
									name="recipientsOption"
									checked={sendMode === SEND_MODE_USERS}
									onChange={() => setSendMode(SEND_MODE_USERS)}
								/>
								Utenti Registrati
							</label>
							<label className="flex items-center gap-2">
								<input
									type="radio"
									name="recipientsOption"
									checked={sendMode === SEND_MODE_SINGLE}
									onChange={() => setSendMode(SEND_MODE_SINGLE)}
								/>
								Specifica destinatari
							</label>
						</div>
						{sendMode === SEND_MODE_SINGLE && (
							<div className="mt-4 ">
								<h4 className="font-semibold mb-2">Destinatari</h4>
								<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
									{recipients.map((email, index) => (
										<input
											key={index}
											type="text"
											value={email}
											onChange={(e) =>
												handleRecipientChange(index, e.target.value)
											}
											className="border border-gray-300 p-2 rounded w-full mb-2"
											placeholder="Inserisci email"
										/>
									))}
								</div>
							</div>
						)}
					</div>

					<div className="flex justify-end gap-4">
						<button
							id="cancel"
							aria-label="Annulla"
							onClick={() => setSelectedTemplate(null)}
							className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
						>
							Annulla
						</button>
						<button
							id="send"
							aria-label="Invia Mail"
							onClick={handleSendMail}
							className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
						>
							Invia Mail
						</button>
					</div>
				</div>
			)}

			{/* Modale di Conferma */}
			{showConfirmModal && (
				<ConfirmModal
					message={confirmationMessage}
					onConfirm={confirmAction}
					onCancel={() => setShowConfirmModal(false)}
				/>
			)}
		</div>
	);
};

export default MailManagement;
